import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Err, Fold } from '../ui.js';
import { useData, blockers } from '../data.js';
import {
  getToday, findDomains, drainOutbox, recommendCadence, createRecord,
  listRecords, ApiError,
} from '../api.js';
import type { TodayResult, QueueItem } from '../api.js';
import { Sweeper } from './Sweeper.js';
import { Capture } from './Capture.js';
import { Setup } from './Setup.js';
import { Link } from 'react-router-dom';

// HOME. Design notes for the queue itself live in src/lib/queue-core.ts — the
// short version is that a queue which can reach zero is a habit and one that
// cannot is a guilt machine.
//
// This page absorbed the old Overview, and the reason is the same fault the
// account record had one level up: THERE WERE TWO HOME PAGES. Today answered
// "what do I do now"; Overview held the opportunity inbox, the pipeline numbers
// and the health of the collectors — which meant the signals a bot found for
// you lived on the page nobody opens. Two places to look is the thing every
// other pass here has been removing.
//
// A rep opening this at 7am asks four questions, and they are now one screen:
//   what do I do          the queue, open, always
//   did anything come in  Inbox
//   how am I doing        Pipeline
//   is the machine on     System

// The emoji icon set is gone. Ten mismatched glyphs was the "mismatched icon
// sets" tell from the anti-cheap list, and none of them said anything the verb
// does not. What replaced them is the RANK — the hierarchy Kyle asked for,
// made visible: the queue is numbered, the top item is drawn as the one to do
// now, and everything below it waits its turn.

export function Today() {
  const [d, setD] = useState<TodayResult | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Per-item progress for batches that act in place: "3 of 10 · JobNimbus".
  const [progress, setProgress] = useState<string | null>(null);
  // "Later" means NOT TODAY, so it is stored against today's date and clears
  // itself overnight. The V1 button was local state: a refresh brought
  // everything back, which teaches you the queue lies to you.
  const SNOOZE_KEY = 'hamer-crm-snoozed';
  const today = new Date().toISOString().slice(0, 10);
  const [done, setDone] = useState<Set<string>>(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(SNOOZE_KEY) ?? '{}') as Record<string, string>;
      return new Set(Object.entries(raw).filter(([, d]) => d === today).map(([k]) => k));
    } catch { return new Set(); }
  });

  const snooze = (id: string) => {
    setDone((s) => new Set(s).add(id));
    try {
      const raw = JSON.parse(localStorage.getItem(SNOOZE_KEY) ?? '{}') as Record<string, string>;
      // Drop yesterday's entries while we are here, so it cannot grow forever.
      const fresh = Object.fromEntries(
        Object.entries(raw).filter(([, d]) => d === today).concat([[id, today]]));
      localStorage.setItem(SNOOZE_KEY, JSON.stringify(fresh));
    } catch { /* a full quota should never break the queue */ }
  };
  const [showLater, setShowLater] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const { companies, settings } = useData();
  const nav = useNavigate();

  const cos = companies ?? [];
  const p1 = cos.filter((x) => x.priority === 'P1');
  const inSeq = cos.filter((x) => x.touches_total > 0);
  const blocked = cos.filter((x) => blockers(x).some((b) => b.hard));
  const unhealthy = settings?.connections.filter((x) => x.status !== 'connected') ?? [];

  const load = () => getToday().then(setD).catch((e) => setError(e.message));
  useEffect(() => { void load(); }, []);

  async function act(i: QueueItem) {
    // Batch rows do the work in place — or take you to the ONE place where
    // the next click does it. Four of the six batch kinds used to fall
    // through to nav('/companies'), so "Do all 10" dumped the operator on a
    // list and did nothing. That was the audit's headline, and Kyle's.
    const action = i.batch?.action;

    if (action === 'find_domains' || action === 'send_all') {
      setBusy(i.id); setError(null);
      try {
        if (action === 'find_domains') {
          const r = await findDomains();
          setDone((s) => new Set(s).add(i.id));
          setProgress(`${r.suggestions.filter((x) => x.domain).length} of ${r.checked} `
            + 'identified. Open each account to accept — nothing was written.');
        } else {
          const r = await drainOutbox();
          // "Sent" and "done" are different words. The drain can legally send
          // zero (cold budget spent, everything deferred) — saying nothing
          // while the row vanished taught the operator the queue lies.
          setProgress(`${r.sent} sent${r.failed ? ` · ${r.failed} FAILED` : ''}`
            + (r.sent === 0 && !r.failed ? ' — nothing was eligible right now (caps or windows).' : '.'));
          if (r.failed) setError(`${r.failed} failed to send.`);
          if (r.sent > 0 || !r.failed) setDone((s) => new Set(s).add(i.id));
        }
        void load();
      } catch (e) {
        setError(e instanceof ApiError ? e.message : String(e));
      } finally { setBusy(null); }
      return;
    }

    // The recommender, run for EVERY account in the row, serially — each one
    // is a model call on the workspace's own key, and a burst of parallel
    // ones is unreadable when the third fails. Uses only the endpoints the
    // account screen itself uses: same audited path, same gates.
    if (action === 'recommend_cadence' && i.batch?.refs?.length) {
      setBusy(i.id); setError(null);
      const refs = i.batch.refs;
      let drafted = 0; const failures: string[] = [];
      for (let n = 0; n < refs.length; n++) {
        const ref = refs[n];
        setProgress(`${n + 1} of ${refs.length} · ${ref.name}…`);
        try {
          const existing = await listRecords<{ id: string }>('sequences', { company_id: ref.id });
          const seqId = existing.records[0]?.id
            ?? (await createRecord<{ id: string }>('sequences', {
              company_id: ref.id, mode: 'A',
              activation_rule: 'Any real dialogue pauses every remaining cold touch on the whole account.',
            })).record.id;
          const r = await recommendCadence(seqId,
            (p) => setProgress(`${n + 1} of ${refs.length} · ${ref.name}: ${p}`));
          drafted++;
          setProgress(`${n + 1} of ${refs.length} · ${ref.name}: ${r.touches} touches drafted`);
        } catch (e) {
          failures.push(`${ref.name}: ${e instanceof ApiError ? e.message : String(e)}`);
        }
      }
      setProgress(`${drafted} of ${refs.length} cadences drafted — every touch is a DRAFT for `
        + 'your review; nothing is armed and nothing sends.'
        + (failures.length ? ` Failed — ${failures.join('; ')}` : ''));
      if (failures.length) setError(`${failures.length} account(s) failed.`);
      setDone((s) => new Set(s).add(i.id));
      setBusy(null);
      void load();
      return;
    }

    // Sourcing people is a judgement call per account, so the button opens
    // the BEST account with the sourcing window already up — the queue
    // re-serves the rest as they are done.
    if (action === 'find_contacts' && i.batch?.refs?.length) {
      nav(`/companies/${i.batch.refs[0].slug}?find=people`);
      return;
    }

    // Ten drafted cadences make an "Arm 10 accounts" batch — and a one-click
    // bulk arm would schedule cold mail nobody reviewed. Agents propose; Kyle
    // promotes. The button opens the best account's sequence for review; the
    // queue re-serves the rest as each is armed or reworked.
    if (action === 'arm' && i.batch?.refs?.length) {
      nav(`/companies/${i.batch.refs[0].slug}`);
      return;
    }

    // Revealing costs a credit a head: land on the contacts screen with the
    // address-less people already filtered, where the bar prices the spend
    // before anything happens.
    if (action === 'reveal') {
      nav('/contacts?filter=no_email');
      return;
    }

    nav(i.href);
  }

  if (error && !d) return <Err>{error}</Err>;
  if (!d) return <p className="loading">Loading…</p>;

  const remaining = d.items.filter((i) => !done.has(i.id));
  const cleared = remaining.length === 0 && d.items.length > 0;

  return (
    <div className="today">
      <header className="todayhead">
        <h1>Today</h1>
        <p className="meta">{cleared ? 'Cleared. That was the day.' : d.headline}</p>
      </header>

      <Err>{error}</Err>
      {progress && <p className="ok-note">{progress}</p>}

      {/* Setup comes before the queue and vanishes when the chain is complete.
          A queue is meaningless to someone who has not told the app what they
          sell — every item in it would be guesswork. */}
      <Setup />

      {/* Reaching zero has to LOOK like something or it is not a reward. */}
      {cleared && (
        <div className="cleared">
          <strong>Done.</strong>
          <span>
            {d.later > 0
              ? `${d.later} more waiting, but not today's work.`
              : 'Nothing else queued. Go sell something.'}
          </span>
        </div>
      )}

      {d.items.length === 0 && (
        <div className="cleared">
          <strong>Nothing waiting.</strong>
          <span>No replies, nothing queued, nothing blocked.</span>
        </div>
      )}

      <ol className="queue">
        {remaining.map((i, n) => (
          <li key={i.id} className={`qitem q-${i.kind}${n === 0 ? ' qtop' : ''}`}>
            {/* The number IS the hierarchy. The first item is the one to do
                now and is drawn like it; a warm CTA on every row would be
                nine equal shouts, which is the flatness Kyle reported. */}
            <span className="qrank" aria-hidden="true">{n + 1}</span>
            <div className="qbody">
              <div className="qline">
                <strong>{i.verb} {i.object}</strong>
                {typeof i.fit === 'number' && <span className="qfit">fit {i.fit}</span>}
                <span className="qmin">{i.minutes} min</span>
              </div>
              <span className="qwhy">{i.why}</span>
              {i.batch && i.batch.names.length > 0 && (
                <span className="qnames">
                  {i.batch.names.join(' · ')}{i.batch.count > i.batch.names.length
                    ? ` and ${i.batch.count - i.batch.names.length} more` : ''}
                </span>
              )}
            </div>
            <button className={n === 0 ? 'primary' : undefined}
              disabled={busy === i.id} onClick={() => void act(i)}>
              {busy === i.id ? 'Working…'
                : i.batch ? (i.batch.cta ?? `Do all ${i.batch.count}`) : 'Open'}
            </button>
            <button className="qsnooze" title="Not today"
              onClick={() => snooze(i.id)}>Later</button>
          </li>
        ))}
      </ol>

      {d.later > 0 && (
        <>
          <button className="linkish" onClick={() => setShowLater(!showLater)}>
            {showLater ? 'Hide' : `${d.later} more below the line`}
          </button>
          {showLater && (
            <ol className="queue later">
              {d.later_items.map((i) => (
                <li key={i.id} className="qitem">
                  <span className="qrank later" aria-hidden="true">·</span>
                  <div className="qbody">
                    <div className="qline"><strong>{i.verb} {i.object}</strong></div>
                    <span className="qwhy">{i.why}</span>
                  </div>
                  <button onClick={() => nav(i.href)}>Open</button>
                </li>
              ))}
            </ol>
          )}
        </>
      )}

      {/* Everything the old Overview held, folded, each header carrying its own
          answer — the same pattern as the account record, for the same reason. */}
      <div className="folds">
        <Fold
          id="inbox"
          title="Inbox"
          summary="opportunities found in email and Slack"
        >
          <div className="actions" style={{ marginBottom: 12 }}>
            <button className="primary" onClick={() => setCapturing(true)}>
              Capture from email or invite
            </button>
            <span className="cost">paste a thread — it becomes records</span>
          </div>
          <Sweeper />
        </Fold>

        <Fold
          id="pipeline"
          title="Pipeline"
          summary={cos.length
            ? `${cos.length} accounts · ${p1.length} P1 · ${inSeq.length} in sequence`
            : 'nothing in the pipeline yet'}
          tone={blocked.length ? 'warn' : null}
        >
          <div className="stats">
            <div className="stat"><span>Accounts</span><strong>{cos.length}</strong></div>
            <div className="stat"><span>P1</span><strong>{p1.length}</strong></div>
            <div className="stat"><span>In sequence</span><strong>{inSeq.length}</strong></div>
            <div className="stat"><span>Hard blocked</span><strong>{blocked.length}</strong></div>
          </div>
          {blocked.length > 0 && (
            <section>
              <h4>Hard blocked</h4>
              {blocked.map((x) => (
                <div className="row" key={x.id}>
                  <Link className="rname" to={`/companies/${x.slug}`}>{x.name}</Link>
                  <span className="flags">
                    {blockers(x).filter((b) => b.hard).map((b) => (
                      <span className="flag flag-block" key={b.label}>{b.label}</span>
                    ))}
                  </span>
                </div>
              ))}
            </section>
          )}
        </Fold>

        <Fold
          id="system"
          title="System"
          summary={unhealthy.length
            ? `${unhealthy.length} connection${unhealthy.length > 1 ? 's' : ''} need attention`
            : 'connections healthy'}
          tone={unhealthy.length ? 'warn' : null}
        >
          {unhealthy.length === 0 && (
            <p className="meta">Everything the CRM depends on is reporting healthy.</p>
          )}
          {unhealthy.map((x) => (
            <div className="row" key={x.id}>
              <span className="rname">{x.provider}</span>
              <span className="meta">{x.meta?.note ?? x.last_error ?? x.status}</span>
              <span className="flags">
                <Link className="flag flag-warn" to="/settings">{x.status.replace('_', ' ')}</Link>
              </span>
            </div>
          ))}
        </Fold>
      </div>

      {capturing && <Capture onClose={() => setCapturing(false)} />}
    </div>
  );
}
